import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { useGetReservationBySitelinkQuery } from '@/core/api/rapidstor';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import Spinner from '@/core/components/elements/spinner';

import classes from '@/scss/screens/find-reservation.module.scss';

export default function FindReservation() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { reference } = useParams();
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState<string>(reference ?? '');

    const {
        data: reservation,
        isFetching,
        isSuccess,
        isError,
    } = useGetReservationBySitelinkQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            id: reference ?? '',
        },
        {
            skip: !reference,
            refetchOnMountOrArgChange: true,
        },
    );

    useEffect(() => {
        if (isSuccess && reservation?.token) {
            navigate(`/move-in/${reservation.token}`, { replace: true });
        }
    }, [isSuccess, reservation, navigate]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        navigate(`/find-reservation/${encodeURIComponent(trimmed)}`);
    };

    // Auto-lookup in progress (e.g. from an email link) — show the spinner.
    if (isFetching) {
        return (
            <div className={`${classes.main}`}>
                <Spinner loader size="60" useDataAttribute />
            </div>
        );
    }

    return (
        <div className={`${classes.main}`}>
            <span className={`${classes.title}`}>{t('wizard:findReservation:title')}</span>
            <p>{t('wizard:findReservation:subtitle')}</p>

            {isError && (
                <Alert type="error" text={t('wizard:findReservation:notFoundError')} />
            )}

            <form className={`${classes.form}`} onSubmit={handleSubmit}>
                <label htmlFor="reference-input">
                    {t('wizard:findReservation:inputLabel')}
                </label>
                <input
                    id="reference-input"
                    type="text"
                    value={inputValue}
                    placeholder={t('wizard:findReservation:placeholder')}
                    onChange={(event) => setInputValue(event.target.value)}
                />
                <Button type="primary" size="medium" disabled={!inputValue.trim()}>
                    {t('wizard:findReservation:submit')}
                </Button>
            </form>
        </div>
    );
}
